/**
 * Environment configuration.
 *
 * Centralizes access to Vite environment variables (client-side).
 * Server-only variables (JWT_SECRET, SMTP_*, etc.) are consumed by the
 * backend and are intentionally NOT read here.
 *
 * Version comes from `package.json` via `@/config/version`.
 */

import { APP_BUILD_TIME, APP_ENVIRONMENT, APP_VERSION } from './version'

const rawEnv = import.meta.env

function readString(key: string, fallback = ''): string {
  const value = (rawEnv as Record<string, unknown>)[key]
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

function readBoolean(key: string, fallback = false): boolean {
  const value = (rawEnv as Record<string, unknown>)[key]
  if (typeof value === 'boolean') return value
  if (value === 'true') return true
  if (value === 'false') return false
  return fallback
}

export const env = {
  appName: readString('VITE_APP_NAME', 'MasterHub'),
  apiUrl: readString('VITE_API_URL'),
  supplierSecret: readString('VITE_SUPPLIER_SECRET'),
  /**
   * Deployment label (development | production). Distinct from
   * `mode`, which Vite derives from the build command.
   */
  environment: readString('VITE_ENVIRONMENT', APP_ENVIRONMENT || rawEnv.MODE),
  version: APP_VERSION,
  buildTime: APP_BUILD_TIME,
  /**
   * Route protection is opt-in. The backend exposes no login endpoint yet, so
   * enabling this before `/auth/login` exists would lock every route.
   */
  authEnabled: readBoolean('VITE_AUTH_ENABLED', false),
  mode: rawEnv.MODE,
  isDev: rawEnv.DEV,
  isProd: rawEnv.PROD,
} as const

export type Env = typeof env
